import { createListenerMiddleware } from '@reduxjs/toolkit';
import { sortingHelper, Filters } from '@ui/src';
import { CountryItem } from '../hoc/settings/types';
import {
  onSortListSuccess as onSortCountriesListSuccess,
  onSortList as onSortCountriesList,
  onFilterList as onFilterCountriesList,
  onFilterListSuccess as onFilterCountriesListSuccess,
} from '../hoc/settings/store/slices/countries';
import {
  onToggleChildren,
  onToggleParent,
} from '../hoc/settings/store/slices/transactions-types';
import countriesFiltersHelper from '../hoc/settings/helpers/filters-helper-countries';
import { PermissionEnum } from '../hoc/user-roles/types';
import permissionsTree from '../hoc/user-roles/variables';
import getPreparePermissions from '../hoc/user-list/helpers/get-permissions';
import sectionActions from '../hoc/user-list/variables';
import {
  onFilterChange,
  onFilterChangeSuccess,
  onPageChange,
  onSectionChange,
} from '../hoc/user-list/store/slices/permissions';
import {
  getUsersWithPerm,
  getUsersWithPermSuccess,
} from '../hoc/user-roles/store/slices/confirm';
import {
  onPageChange as onRolesPageChange,
  onSectionChange as onRolesSectionChange,
} from '../hoc/user-roles/store/slices/roles';

import { AdministrationRootState } from './state';

const administrationMiddleware = createListenerMiddleware();

administrationMiddleware.startListening({
  actionCreator: onSortCountriesList,
  effect: async (action, listenerApi) => {
    const {
      settings: {
        countries: { values },
      },
    } = listenerApi.getState() as AdministrationRootState;

    if (values) {
      const nextStatements = sortingHelper<CountryItem>(
        [...values],
        action.payload.name,
        action.payload.direction,
      );

      setTimeout(() => {
        listenerApi.dispatch(onSortCountriesListSuccess(nextStatements));
        listenerApi.cancelActiveListeners();
      }, 1000);
    }
  },
});

administrationMiddleware.startListening({
  actionCreator: onFilterCountriesList,
  effect: async (action, listenerApi) => {
    const {
      settings: {
        countries: { sort, values },
      },
    } = listenerApi.getState() as AdministrationRootState;

    const nextFilters: Filters = {};

    Object.keys(action.payload).forEach((key) => {
      if (action.payload[key] && action.payload[key].value.length < 2) {
        nextFilters[key] = {
          ...nextFilters[key],
          value: [action.payload[key].value.join()],
        };
      } else {
        nextFilters[key] = action.payload[key];
      }
    });

    const nextStatements = countriesFiltersHelper(
      values as CountryItem[],
      sort?.sortProp as string,
      sort?.sortDir as 'asc' | 'desc',
      nextFilters,
    );

    setTimeout(() => {
      listenerApi.dispatch(onFilterCountriesListSuccess(nextStatements));
      listenerApi.cancelActiveListeners();
    }, 1000);
  },
});

administrationMiddleware.startListening({
  actionCreator: onToggleChildren,
  effect: async (action, listenerApi) => {
    const {
      settings: {
        transactionsTypes: { values },
      },
    } = listenerApi.getState() as AdministrationRootState;

    if (values) {
      const nextType = values?.find(
        (item) => item.id === action.payload.typeId,
      );

      let isEqualActive = false;

      if (nextType && nextType.children) {
        isEqualActive = nextType.children.every(
          (item) => item.active === false,
        );
      }

      setTimeout(() => {
        if (isEqualActive) {
          listenerApi.dispatch(onToggleParent(action.payload.typeId));
        }
        listenerApi.cancelActiveListeners();
      }, 1000);
    }
  },
});

administrationMiddleware.startListening({
  actionCreator: onSectionChange,
  effect: async (action, listenerApi) => {
    const columnsData = getPreparePermissions(permissionsTree);

    const pageKeys = Object.keys(columnsData[action.payload]?.pages || {});
    const firstPageKey = pageKeys.length
      ? (pageKeys[0] as PermissionEnum)
      : undefined;

    listenerApi.dispatch(onPageChange(firstPageKey));
    listenerApi.cancelActiveListeners();
  },
});

administrationMiddleware.startListening({
  actionCreator: onFilterChange,
  effect: async (action, listenerApi) => {
    const {
      userList: {
        permissions: { search, sectionId },
      },
    } = listenerApi.getState() as AdministrationRootState;
    const permissions = sectionActions[sectionId];

    const nextPerm =
      permissions &&
      Object.entries(permissions)
        ?.filter((item) =>
          item[1].title
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase()),
        )
        .reduce(
          (acc, curValue) => ({ ...acc, [curValue[0]]: curValue[1] }),
          {},
        );

    listenerApi.dispatch(onFilterChangeSuccess(nextPerm));
    listenerApi.cancelActiveListeners();
  },
});

administrationMiddleware.startListening({
  actionCreator: getUsersWithPerm,
  effect: async (action, listenerApi) => {
    setTimeout(() => {
      listenerApi.dispatch(
        getUsersWithPermSuccess({ default: 10, custom: 15 }),
      );
      listenerApi.cancelActiveListeners();
    }, 1000);
  },
});

administrationMiddleware.startListening({
  actionCreator: onRolesSectionChange,
  effect: async (action, listenerApi) => {
    const columnsData = getPreparePermissions(permissionsTree);

    const pageKeys = Object.keys(columnsData[action.payload]?.pages || {});
    const firstPageKey = pageKeys.length
      ? (pageKeys[0] as PermissionEnum)
      : undefined;

    listenerApi.dispatch(onRolesPageChange(firstPageKey));
    listenerApi.cancelActiveListeners();
  },
});

export default administrationMiddleware;
